import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  Button,
  Badge,
  Spinner,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  Banner
} from '@shopify/polaris';
import axios from 'axios';
import { AppContext } from '../App';

function OrderDetailPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { shop, setShop, showToast, API } = useContext(AppContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [labelCreated, setLabelCreated] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState(null);

  useEffect(() => {
    const shopParam = searchParams.get('shop') || localStorage.getItem('shop');
    if (shopParam) {
      setShop(shopParam);
      fetchOrder(shopParam);
    } else {
      navigate('/');
    }
  }, [orderId]);

  const fetchOrder = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/orders/${encodeURIComponent(orderId)}`, {
        params: { shop: shopDomain }
      });
      setOrder(response.data);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to fetch order', true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLabel = async () => {
    setCreatingLabel(true);
    try {
      const response = await axios.post(
        `${API}/labels/create`,
        null,
        {
          params: {
            shop: shop,
            order_id: orderId
          }
        }
      );
      setTrackingNumber(response.data.tracking_number);
      setLabelCreated(true);
      showToast('Shipping label created successfully!');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create label', true);
    } finally {
      setCreatingLabel(false);
    }
  };

  const handleDownloadLabel = async () => {
    try {
      const response = await axios.get(
        `${API}/labels/download/${trackingNumber}`,
        {
          params: { shop: shop },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `label_${trackingNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      showToast('Failed to download label', true);
    }
  };

  if (loading) {
    return (
      <Page title="Order Details">
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  if (!order) {
    return (
      <Page title="Order Not Found">
        <Card>
          <p>Order not found</p>
        </Card>
      </Page>
    );
  }

  const shippingAddress = order.shippingAddress || {};
  const totalPrice = order.totalPriceSet?.shopMoney?.amount || '0';
  const currency = order.totalPriceSet?.shopMoney?.currencyCode || 'AUD';

  return (
    <Page
      title={`Order ${order.name}`}
      backAction={{ onAction: () => navigate(`/orders?shop=${shop}`) }}
    >
      <BlockStack gap="400">
        {labelCreated && (
          <Banner tone="success" data-testid="label-success-banner">
            <p>Shipping label created successfully! Tracking number: {trackingNumber}</p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h2">Order Information</Text>
              <Badge tone="attention">{order.displayFulfillmentStatus}</Badge>
            </InlineStack>
            <Divider />
            <InlineStack gap="200">
              <Text variant="bodyMd" as="span" fontWeight="semibold">Total:</Text>
              <Text variant="bodyMd" as="span">{currency} {parseFloat(totalPrice).toFixed(2)}</Text>
            </InlineStack>
            <InlineStack gap="200">
              <Text variant="bodyMd" as="span" fontWeight="semibold">Customer:</Text>
              <Text variant="bodyMd" as="span">
                {order.customer ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() : 'N/A'}
              </Text>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Shipping Address</Text>
            <Divider />
            <BlockStack gap="100">
              <Text variant="bodyMd" as="p">
                {shippingAddress.firstName} {shippingAddress.lastName}
              </Text>
              <Text variant="bodyMd" as="p">{shippingAddress.address1}</Text>
              {shippingAddress.address2 && (
                <Text variant="bodyMd" as="p">{shippingAddress.address2}</Text>
              )}
              <Text variant="bodyMd" as="p">
                {shippingAddress.city}, {shippingAddress.province} {shippingAddress.zip}
              </Text>
              <Text variant="bodyMd" as="p">{shippingAddress.country}</Text>
              {shippingAddress.phone && (
                <Text variant="bodyMd" as="p">Phone: {shippingAddress.phone}</Text>
              )}
            </BlockStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Shipping Label</Text>
            <Divider />
            {!labelCreated ? (
              <Button
                primary
                onClick={handleCreateLabel}
                loading={creatingLabel}
                data-testid="create-label-btn"
              >
                Create Shipping Label
              </Button>
            ) : (
              <InlineStack gap="200">
                <Button
                  onClick={handleDownloadLabel}
                  data-testid="download-label-btn"
                >
                  Download Label
                </Button>
                <Text variant="bodyMd" as="p" tone="subdued">
                  Tracking: {trackingNumber}
                </Text>
              </InlineStack>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

export default OrderDetailPage;